import { useState } from "react";
import { Download, ExternalLink, X } from "lucide-react";
import { InstallGuide } from "@/components/install/InstallGuide";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useInstall } from "@/lib/install";

const PROD = process.env.REACT_APP_PRODUCTION_URL;
const onPreview = () => !!PROD && window.location.hostname.endsWith(".preview.emergentagent.com");

export const InstallDialog = ({ open, onOpenChange }) => {
  const { canPrompt, prompt, platform } = useInstall();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="install-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Scarica l'app K Gestionale</DialogTitle>
          <DialogDescription>Gratis e senza App Store: guarda l'animazione e ripeti i passaggi sul tuo telefono.</DialogDescription>
        </DialogHeader>
        {onPreview() && (
          <div className="rounded-xl bg-secondary/70 p-3 text-sm flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="install-dialog-preview">
            <span>Questo è il link di prova: installa l'app da quello ufficiale.</span>
            <a href={PROD} className="inline-flex items-center gap-1 font-semibold text-primary" data-testid="install-dialog-open-prod">
              Apri l'app ufficiale <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        )}
        {canPrompt && !onPreview() && (
          <Button className="w-full h-11" onClick={async () => { if (await prompt()) onOpenChange(false); }} data-testid="install-now-button">
            <Download className="w-4 h-4 mr-2" /> Installa ora con un click
          </Button>
        )}
        <InstallGuide initial={platform} />
      </DialogContent>
    </Dialog>
  );
};

export const InstallAppButton = ({ className = "", variant = "outline", label = "Scarica l'app", testid = "install-app-button", compact = false }) => {
  const { installed } = useInstall();
  const [open, setOpen] = useState(false);
  if (installed) return null;
  return (
    <>
      <Button variant={variant} size={compact ? "sm" : "default"} onClick={() => setOpen(true)} data-testid={testid} className={`gap-1.5 ${className}`}>
        <Download className="w-4 h-4" />
        {compact ? <span className="hidden sm:inline">{label}</span> : label}
      </Button>
      <InstallDialog open={open} onOpenChange={setOpen} />
    </>
  );
};

// Scheda nella Home del telefono: invita a installare l'app (si può chiudere).
export const InstallAppCard = () => {
  const { installed } = useInstall();
  const [hidden, setHidden] = useState(() => localStorage.getItem("install-card") === "off");
  if (installed || hidden) return null;
  const close = () => { localStorage.setItem("install-card", "off"); setHidden(true); };
  return (
    <div className="relative rounded-2xl border border-primary/30 bg-primary/5 p-4 flex items-center gap-3" data-testid="install-app-card">
      <img src="/icon-192.png" alt="" className="w-11 h-11 rounded-xl shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold leading-tight">Scarica l'app</div>
        <div className="text-xs text-muted-foreground">Aprila con un tocco dalla schermata Home</div>
      </div>
      <InstallAppButton variant="default" label="Scarica" testid="install-app-card-button" />
      <button type="button" onClick={close} aria-label="Chiudi" className="absolute top-1.5 right-1.5 p-1 text-muted-foreground hover:text-foreground" data-testid="install-app-card-close">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default function InstallPrompt() {
  return <InstallAppButton compact label="Installa app" />;
}
